<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Time Tracking') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'ProjectSettings|Time tracking allows project members to log the time spent working on tasks. This helps monitor project progress and analyze time allocation.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|How It Works') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|When enabled, team members can record the time they spend on each task. This data helps track actual effort and provides insights into project efficiency.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Duration Format Examples') }}</h4>
            <p style="margin-bottom: 8px">
              {{ s__('ProjectSettings|You can enter duration in various formats:') }}
            </p>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 6px">
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2h30m</code
                >
                {{ s__('ProjectSettings|or') }}
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2ч30м</code
                >
                {{ s__('ProjectSettings|(2 hours 30 minutes)') }}
              </li>
              <li style="margin-bottom: 6px">
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2:30</code
                >
                {{ s__('ProjectSettings|(hours:minutes format)') }}
              </li>
              <li style="margin-bottom: 6px">
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >150m</code
                >
                {{ s__('ProjectSettings|or') }}
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >150м</code
                >
                {{ s__('ProjectSettings|(minutes only)') }}
              </li>
              <li style="margin-bottom: 6px">
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2.5h</code
                >
                {{ s__('ProjectSettings|or') }}
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2.5ч</code
                >
                {{ s__('ProjectSettings|(decimal hours)') }}
              </li>
              <li style="margin-bottom: 6px">
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2h</code
                >
                {{ s__('ProjectSettings|or') }}
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >2ч</code
                >
                {{ s__('ProjectSettings|(hours only)') }}
              </li>
              <li style="margin-bottom: 6px">
                <code
                  style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-size: 13px"
                  >30</code
                >
                {{ s__('ProjectSettings|(plain number, treated as minutes)') }}
              </li>
            </ul>

            <h4>{{ s__('ProjectSettings|Benefits') }}</h4>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 8px">
                {{
                  s__(
                    'ProjectSettings|Track actual time spent on tasks for better project planning'
                  )
                }}
              </li>
              <li style="margin-bottom: 8px">
                {{
                  s__(
                    'ProjectSettings|Generate time reports for billing, analysis, or resource planning'
                  )
                }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Identify tasks that take longer than expected') }}
              </li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('ProjectSettings|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.info-section ul {
  line-height: 1.6;
}
</style>
